import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ApiConfig {
  name: string;
  url: string;
  description: string;
  isDefault?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private readonly STORAGE_KEY = 'agrofuturo_api_config';

  // URLs predefinidas
  private readonly predefinedConfigs: ApiConfig[] = [
    {
      name: 'Producción',
      url: 'https://dev.mercosys.com.py/agrofuturo/api',
      description: 'Servidor de producción principal'
    },
    {
      name: 'Desarrollo',
      url: 'http://dev.mercosys.com.py:3300/api',
      description: 'Servidor de desarrollo y pruebas'
    },
    {
      name: 'Local',
      url: 'http://localhost:3300/api',
      description: 'Servidor local para desarrollo',
      isDefault: true
    }
  ];

  private currentConfigSubject = new BehaviorSubject<ApiConfig>(this.predefinedConfigs[0]);
  public currentConfig$ = this.currentConfigSubject.asObservable();

  constructor() {
    this.loadSavedConfig();
  }

  /**
   * Obtiene todas las configuraciones predefinidas
   */
  getPredefinedConfigs(): ApiConfig[] {
    return [...this.predefinedConfigs];
  }

  /**
   * Obtiene la configuración actual
   */
  getCurrentConfig(): ApiConfig {
    return this.currentConfigSubject.value;
  }

  /**
   * Obtiene la URL actual de la API
   */
  getCurrentApiUrl(): string {
    return this.currentConfigSubject.value.url;
  }

  /**
   * Cambia la configuración de la API
   */
  setApiConfig(config: ApiConfig): void {
    this.currentConfigSubject.next(config);
    this.saveConfig(config);
    console.log('ConfigService: Configuración cambiada a:', config.name, config.url);
  }

  /**
   * Cambia la configuración por nombre
   */
  setApiConfigByName(configName: string): boolean {
    const config = this.predefinedConfigs.find(c => c.name === configName);
    if (config) {
      this.setApiConfig(config);
      return true;
    }
    return false;
  }

  /**
   * Agrega una configuración personalizada
   */
  addCustomConfig(config: ApiConfig): void {
    // Verificar si ya existe
    const existingIndex = this.predefinedConfigs.findIndex(c => c.name === config.name);
    if (existingIndex >= 0) {
      this.predefinedConfigs[existingIndex] = config;
    } else {
      this.predefinedConfigs.push(config);
    }
  }

  /**
   * Restaura la configuración por defecto
   */
  resetToDefault(): void {
    const defaultConfig = this.predefinedConfigs.find(c => c.isDefault) || this.predefinedConfigs[0];
    this.setApiConfig(defaultConfig);
  }

  /**
   * Verifica si la URL actual es válida
   */
  async testApiConnection(url?: string): Promise<boolean> {
    const testUrl = url || this.getCurrentApiUrl();
    try {
      // Primero intentamos con /health
      try {
        const response = await fetch(`${testUrl}/health`, { method: 'GET' });
        if (response.ok) return true;
      } catch (healthError) {
        console.log('ConfigService: Endpoint /health no disponible, probando raíz');
      }

      // Si /health falla, intentamos con la raíz
      const rootResponse = await fetch(testUrl, { method: 'GET' });
      return rootResponse.ok;
    } catch (error) {
      console.warn('ConfigService: Error al probar conexión:', error);
      return false;
    }
  }

  /**
   * Carrega a configuração salva do localStorage
   */
  private loadSavedConfig(): void {
    try {
      const savedConfig = localStorage.getItem(this.STORAGE_KEY);
      if (savedConfig) {
        const config: ApiConfig = JSON.parse(savedConfig);
        // Verificar se a configuração salva ainda existe nas predefinidas
        const existingConfig = this.predefinedConfigs.find(c => c.url === config.url);
        if (existingConfig) {
          this.currentConfigSubject.next(existingConfig);
        } else {
          // Se não existe mais, adicionar como configuração personalizada
          this.predefinedConfigs.push(config);
          this.currentConfigSubject.next(config);
        }
      }
    } catch (error) {
      console.warn('ConfigService: Erro ao carregar configuração salva:', error);
    }
  }

  /**
   * Salva a configuração no localStorage
   */
  private saveConfig(config: ApiConfig): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.warn('ConfigService: Erro ao salvar configuração:', error);
    }
  }
}
